import { Link } from "react-router-dom";
import { Badge, Button, Drawer } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerListItem } from "@/lib/api/types";
import { StoreKindTag } from "./ui";

type SellerDetailDrawerProps = {
  seller: SellerListItem | null;
  locale: string;
  onClose: () => void;
  /** Opens the full seller page. Owned by the caller, which has the router. */
  onViewSeller: (id: string) => void;
};

/**
 * A seller's details over the list. A drawer rather than a navigation: the
 * admin is working THROUGH the list and should not lose their place in it.
 * The full page (editing, invites, adding a business) is one click away.
 */
export function SellerDetailDrawer({
  seller,
  locale,
  onClose,
  onViewSeller,
}: SellerDetailDrawerProps) {
  const t = useTranslations();

  return (
    <Drawer
      open={Boolean(seller)}
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      crumb={seller ? `${t(keys.admin.sellerAccountsTitle)} / ${seller.name}` : undefined}
      badge="team"
      title={seller?.name}
      sub={seller ? `${t(keys.admin.storesTitle)}: ${seller.stores.length}` : undefined}
      actions={
        seller ? (
          <>
            <Button variant="primary" size="sm" onClick={() => onViewSeller(seller.id)}>
              {t(keys.admin.viewSeller)}
            </Button>
            <Button variant="ghost" size="sm" muted onClick={onClose}>
              {t(keys.common.close)}
            </Button>
          </>
        ) : null
      }
    >
      {seller ? (
        <>
          <dl className="rd-kv">
            <dt>{t(keys.common.email)}</dt>
            <dd style={{ overflowWrap: "anywhere" }}>
              {/* Latin string: it keeps its own direction on the Arabic page. */}
              {seller.email ? <bdi dir="ltr">{seller.email}</bdi> : "—"}
            </dd>
            <dt>{t(keys.common.phone)}</dt>
            <dd className="rd-num" style={{ textAlign: "start" }}>
              <bdi dir="ltr">{seller.phone}</bdi>
            </dd>
          </dl>
          {seller.stores.length > 0 ? (
            <div>
              <h3 style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 600 }}>
                {t(keys.admin.storesTitle)}
              </h3>
              {seller.stores.map((store) => (
                <Link
                  key={store.id}
                  to={`/${locale}/admin/stores/${store.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 0",
                    borderBottom: "1px solid var(--rd-line-row)",
                    color: "var(--rd-text)",
                    textDecoration: "none",
                  }}
                >
                  <Badge
                    id={store.storeKind === "RESTAURANT" ? "servicebell" : "cart-world"}
                    size={28}
                  />
                  <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                    <span style={{ fontWeight: 500 }} dir="auto">
                      {store.storeName}
                    </span>
                    <bdi dir="ltr" className="rd-cell-sub" style={{ textAlign: "start" }}>
                      {store.storeSlug}
                    </bdi>
                  </span>
                  <StoreKindTag kind={store.storeKind} />
                </Link>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
}
